import styled from '@emotion/styled';
import ApolloForm from '$/components/ApolloForm';
import Logo from '$/components/Logo';
import React from 'react';
import tw from 'twin.macro';
import UserMenu from './UserMenu';
import {
  UpdateEwebinarInput,
  useUpdateEwebinarMutation,
  EWebinarInfoFragment,
} from '$/GraphQL/schema/schema';
import EmailConfirmationModal from '../../components/EmailConfirmationModal';
import { css } from '@emotion/react';
import { PermissionedLink } from '../../components/Router';
import isMobile from '$/utils/isMobile';
import TitleInput from '../../components/ui/Input/TitleInput';
import DashboardTitle from './Dashboard/DashboardTitle';
import { BackIcon } from '$/images/Icons';
import { MarketingHeader } from '../Credentials/Login/MarketingHeader';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { useHistoryPath } from '$/hooks/useHistory';
import { useHomePageContext } from './HomePage/HomePageContext';
import { useMe } from '$/hooks/useMe';
import Form from '$/components/ui/Form';
interface UpdateTitleFormProps extends React.ComponentProps<typeof ApolloForm> {
  ewebinar: EWebinarInfoFragment;
  disabled: boolean;
}

const UpdateTitleForm: React.FC<UpdateTitleFormProps> = ({ ewebinar, disabled }) => {
  const [updateEwebinarMutation] = useUpdateEwebinarMutation();

  return (
    <ApolloForm
      className='flex w-full flex-row items-center'
      resetAfterSave={false}
      submitMode={Form.SubmitMode.onChangeDebounced}
      mode='onChange'
      defaultValues={
        {
          id: ewebinar.id,
          title: ewebinar.title,
        } as UpdateEwebinarInput
      }
      onSubmit={async (data: UpdateEwebinarInput): Promise<void> => {
        await updateEwebinarMutation({ variables: { data } });
      }}
    >
      <Form.Field
        name='title'
        className='w-full center_header mb-0'
        component={<TitleInput placeholder='Untitled webinar' disabled={disabled} />}
      />
    </ApolloForm>
  );
};

interface BackTitleProps {
  className?: string;
  text?: string;
}
const BackTitle = styled(({ className, text }: BackTitleProps) => (
  <div className={`${className} flex items-baseline`}>
    <span className='min-w-1e inline-block mr-1_125e'>
      <BackIcon />
    </span>
    {text && <span className='text-2xl font-title'>{text}</span>}
  </div>
))``;

interface HeaderProps {
  className?: string;
  linkTo?: string;
  linkText?: string;
  ewebinar?: EWebinarInfoFragment;
  canEdit?: boolean;
  area?: string;
  wide?: boolean;
  setId?: string;
  isDashboard?: boolean;
  showUserMenu?: boolean;
  showMarketingMenus?: boolean;
}

const Header = styled<React.FC<HeaderProps>>(
  ({
    className,
    linkText,
    linkTo,
    wide,
    showUserMenu = true,
    showMarketingMenus,
    ewebinar,
    area,
    canEdit,
    setId,
    isDashboard,
  }: HeaderProps) => {
    const homePath = useHistoryPath('/home');
    const backTo = linkTo ?? '/home' + (homePath?.search ?? '');
    const container = wide ? 'wide-container' : 'container';

    const etheme = useThemeContext();
    const { noPublishedWebinars } = useHomePageContext();
    const me = useMe();

    if (!me) {
      return null;
    }

    const showRewards = noPublishedWebinars > 0 && !me.integrations?.firstPromoterToken;

    return (
      <div
        className={`${container} ${className} ${!isMobile ? 'my-3/4e' : 'mb-1'}`}
        css={css`
          ${tw`items-start md:items-center`}
          height: 34px;
        `}
      >
        <div
          className='Left flex flex-1 w-full items-center flex-row'
          css={css`
            color: ${etheme.header.fg};
            a#backLink {
              color: ${etheme.header.fg};
              &:hover {
                color: ${etheme.header.fgHover};
              }
            }
            .header-title,
            .dashboard-title .text {
              ${tw`
                whitespace-no-wrap
                appearance-none
                font-title
                font-light
                text-2xl
                bg-transparent
                z-20
              `}
              ${!ewebinar ? 'flex-grow: 1; text-align: center;' : ''}
            }
          `}
        >
          {!isMobile ? (
            <>
              {showMarketingMenus ? (
                <MarketingHeader
                  isLoggedIn
                  show={['usecases', 'resources', showRewards ? 'cash' : undefined]}
                />
              ) : (
                <PermissionedLink id='backLink' to={backTo}>
                  {linkText ? (
                    <BackTitle text={linkText} />
                  ) : Boolean(ewebinar) ? (
                    <BackTitle />
                  ) : (
                    <Logo
                      noLink
                      heightPx={24}
                      version={ewebinar ? 'icon' : 'normal'}
                      className='mx-auto'
                    />
                  )}
                </PermissionedLink>
              )}
              {area && <span className='header-title mr-1/4e'>{area}</span>}
              {ewebinar &&
                !linkText &&
                (isDashboard && setId ? (
                  <DashboardTitle setId={setId} />
                ) : (
                  <UpdateTitleForm ewebinar={ewebinar} disabled={!canEdit} />
                ))}
            </>
          ) : (
            <PermissionedLink to={linkTo || '/home'}>
              <Logo heightPx={1.5 * 14} className='mx-auto' version='normal' noLink />
            </PermissionedLink>
          )}
        </div>
        <div className='Right ml-10 leading-0'>{showUserMenu && <UserMenu />}</div>
        <EmailConfirmationModal />
      </div>
    );
  }
)`
  ${tw`mx-auto flex justify-between items-center`}
`;

export default Header;
